import React, { useMemo } from 'react';
import { Redirect, useParams } from 'react-router-dom';

import { OverlaySpinner } from '@app/common/components/overlayspinner';
import { PageLayout } from '@app/common/components/PageLayout';
import { isString } from '@app/common/utils';

import { isEmbeddedMode } from '@app/core/authentication/embeddedMode/embeddedMode';

import { NotifierWrapper } from '@app/widgets/contacts-and-settings/components/NotifierWrapper';
import {
  DetailAddressFieldLocator,
} from '@app/widgets/contacts-and-settings/fields/Address/AddressGroupDetails';
import {
  DetailEmailFieldLocator,
} from '@app/widgets/contacts-and-settings/fields/Email/EmailFieldDetails';
import {
  DetailEmailFieldInfoGroupItemLocator,
} from '@app/widgets/contacts-and-settings/fields/Email/EmailFieldListItemDetails';
import {
  PhoneFieldDetailsLocator,
} from '@app/widgets/contacts-and-settings/fields/Phone/PhoneFieldDetails';
import {
  EditPasswordFieldLocator,
} from '@app/widgets/contacts-and-settings/fields/ProfileAndSecurity/Security/Password/EditPasswordField';
import {
  userEventsLogsField,
} from '@app/widgets/contacts-and-settings/fields/ProfileAndSecurity/Security/UserEventLogs/DisplayUserEventLogField';
import {
  UserEventLogs,
} from '@app/widgets/contacts-and-settings/fields/ProfileAndSecurity/Security/UserEventLogs/UserEventLogs';
import { useUserSettingsContextField } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsContextField';
import { useUserSettingsLocator } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsLocator';
import { PasswordFields, UrlParams } from '@app/widgets/contacts-and-settings/types';
import { getUrlToMenuItem } from '@app/widgets/contacts-and-settings/utils/paths';
import { RenderSection } from '@app/widgets/contacts-and-settings/utils/settingsSections';
import { useGetLoginSettingsQuery } from '@app/widgets/user-login/LoginWizard/steps/UsernamePassword/queries/queryTyping/get-login-settings';
import { checkPasskeyAvaialble } from '@app/widgets/user-login/LoginWizard/steps/UsernamePassword/utils';

import { RegisterPasskeyField } from '../../fields/ProfileAndSecurity/Passkeys/RegisterPasskeyField';
import { TimeZone } from '../../fields/ProfileAndSecurity/UserProfile/TimeZone/TimeZone';
import { TIME_ZONE_SECTION_ID } from '../../fields/ProfileAndSecurity/UserProfile/UserProfileList';

/**
 * Return true if the field is passkey and passkey is enabled and not in embedded mode, as
 * passkey settings are temporary exluded for native apps.
 */
export const isPasskeyRender = (fieldId: string, isPasskeyEnabled: boolean, isEmbedded: boolean) => (
  fieldId === PasswordFields.Passkey && isPasskeyEnabled && !isEmbedded
);

export const FieldDetails: React.FC = () => {
  const { fieldId } = useParams<UrlParams>();
  const { sectionId, currentSection, sectionField } = useUserSettingsContextField();
  const locator = useUserSettingsLocator([
    EditPasswordFieldLocator,
    DetailEmailFieldInfoGroupItemLocator,
    DetailEmailFieldLocator,
    DetailAddressFieldLocator,
    PhoneFieldDetailsLocator,
  ]);

  // get current login settings
  const { loading, data: loginSettingsData } = useGetLoginSettingsQuery({
    fetchPolicy: 'cache-and-network',
  });
  const isPasskeyEnabled = loginSettingsData?.loginSettings.passkeyLoginSettings.isEnabled && checkPasskeyAvaialble();

  const backRoute = useMemo(() => getUrlToMenuItem(
    sectionId,
  ), [sectionId]);

  const renderDetails = useMemo(() => {
    if (sectionField) {
      const Component = locator(sectionField);

      return (
        <Component
          parent=""
          field={sectionField}
          locator={locator}
        />
      );
    }

    if (fieldId === userEventsLogsField.id) {
      return <UserEventLogs />;
    }

    if (fieldId === TIME_ZONE_SECTION_ID) {
      return <TimeZone backRoute={backRoute} />;
    }

    if (isPasskeyRender(fieldId, !!isPasskeyEnabled, isEmbeddedMode())) {
      return <RegisterPasskeyField />;
    }

    return (
      <Redirect to={backRoute} />
    );
  }, [sectionField, backRoute]);

  // returns common title or customized for specyfic field
  const getTitle = (title: RenderSection['subTitle']) => (
    isString(title) ? title : title.get(fieldId)
  );

  if (loading) {
    return (
      <OverlaySpinner
        visible
      />
    );
  }

  return (
    <PageLayout
      title={currentSection && getTitle(currentSection.subTitle)}
      backRoute={backRoute}
    >
      <NotifierWrapper />
      {renderDetails}
    </PageLayout>
  );
};
